// The trip for a child, two ways (mockup 02). "Today": one day's plans in time order, with a
// strip of days and BOOKED / WANT TO GO / 🎁 tags (domain/trip/dayPlan). "All days": with the
// world stage live a chain of 3D islands (one per stage) that scrolls with this page, each
// island beside a frosted label and legs as chips between; in Calm/Still or without WebGL the
// stages are flat bands; tapping one opens it on the Map (WI-04). Both read the trip as this child
// may see it (domain/trip/visible).
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import type { Leg, Pack, Stage } from '../../shared/pack/schema'
import { goTab, openOverlay, useRoute } from '../app/router'
import { mapView } from '../map/bindings'
import { journeyStops } from '../map/journey'
import type { TripNow } from '../app/useTripNow'
import { LegIcon } from '../art/icons'
import type { Person } from '../../shared/events'
import { useToast } from '../app/toast'
import { playsChime } from '../audio/synth'
import { checkIn } from '../data/checkin'
import { useChildSettings, useSurpriseBursts } from '../data/controls'
import { useEvents } from '../data/progress'
import { playDay } from '../domain/arcade/playDay'
import { myPlaces } from '../domain/places'
import { rewardRules } from '../domain/rewards'
import { dateRange, daysBetween, zoned } from '../domain/time'
import { tripZone } from '../../shared/tripZone'
import { stageStatuses } from '../domain/stages'
import { dayPlan, type PlanTag } from '../domain/trip/dayPlan'
import { MYSTERY_SPOT, opensIn } from '../domain/trip/visible'
import { goStop, useStage } from '../stage/bridge'
import type { TripAnchor } from '../stage/sets/tripSet'
import { fmt, t } from '../strings/en'
import { playsOf } from '../domain/players'
import { onTrip } from '../domain/trip/travellers'
import { pieceHint } from '../domain/trip/kitRoutes'
import { useSession } from '../data/session'
import { figureFor } from '../../shared/figures'
import { KidHookLine } from './KidHookLine'
import { When } from './When'

/** A stop tapped here opens on the Map (WI-04): its island, and the stop bar on it. */
function openStop(pack: Pack, stageId: string) {
  const stops = journeyStops(mapView(pack).itinerary.stages)
  const i = stops.findIndex((s) => s.stageId === stageId)
  if (i < 0) return
  goStop(i, stops[i]!.spot)
  goTab('map')
}

/** "Base town, resort village" → "Base town"; "Sapporo Station" → "Sapporo". */
const shortStop = (name: string) => name.split(',')[0]!.replace(/( Airport)? Station$/, '')
/** Wall-clock time as written in the leg (its own timezone). */
const localTime = (iso: string) => iso.slice(11, 16)

/**
 * Mockup style: flights by code with both times, trains by town, buses and lifts by time and
 * where they go (WI-30: a chip says its destination).
 */
function legLabel(leg: Leg): string {
  // An untimed journey (WI-42: times to come) says where it goes, with no clock.
  if (leg.untimed) return leg.kind === 'bus' || leg.kind === 'lift' ? `→ ${shortStop(leg.to.name)}` : `${shortStop(leg.from.name)} → ${shortStop(leg.to.name)}`
  const dep = localTime(leg.departsAt)
  if (leg.kind === 'flight') return `${dep} ${leg.from.code} → ${shortStop(leg.to.name)} ${localTime(leg.arrivesAt)}`
  if (leg.kind === 'bus' || leg.kind === 'lift') return `${dep} → ${shortStop(leg.to.name)}`
  return `${dep} ${shortStop(leg.from.name)} → ${shortStop(leg.to.name)}`
}

/**
 * WI-40 #5 / WI-41: one chip per link covering every leg on it (🚌 10:05 → Kutchan, 🚆 11:02 → Otaru),
 * a line a leg, placed beside the link's track, never across it.
 */
export function JourneyChip({ legs, link }: { legs: readonly Leg[]; link?: string }) {
  const body = legs.map((l) => (
    <span key={l.id} className="journey-leg" aria-hidden>
      <LegIcon kind={l.kind} />
      {`${localTime(l.departsAt)} → ${shortStop(l.to.name).replace(/ (International )?Airport$/, '')}`}
    </span>
  ))
  const label = legs.map(legLabel).join(', ')
  // On the 3D chain the chip is the journey's button (§8.3: keyboard and screen readers get the act a tap gives).
  if (!link) return <span className="pill journey-chip" aria-label={label}>{body}</span>
  const play = () => {
    const trip = useStage.getState().trip
    useStage.getState().set({ trip: { ...trip, play: { link, n: (trip.play?.n ?? 0) + 1 } } })
  }
  return (
    <button type="button" className="pill journey-chip press" aria-label={t.trip.playJourney(label)} onClick={play}>
      {body}
    </button>
  )
}

export function LegChip({ leg }: { leg: Leg }) {
  return (
    <span className="pill leg-chip">
      <LegIcon kind={leg.kind} />
      {legLabel(leg)}
    </span>
  )
}

/** Transfer legs sit before the stage they arrive into; the last day's legs go after the trip. */
export function legsAround(pack: Pack): { before: Map<string, Leg[]>; after: Leg[] } {
  const before = new Map<string, Leg[]>()
  const after: Leg[] = []
  const stages = pack.itinerary.stages
  for (const leg of pack.itinerary.legs) {
    // The day where the trip is when it leaves (B13: a stage may have its own zone).
    const ms = Date.parse(leg.departsAt)
    const date = zoned(ms, tripZone(pack, ms)).date
    const into = stages.find((s, i) => i > 0 && s.startDate === date) ?? (date === pack.meta.startDate ? stages[0] : undefined)
    if (into) before.set(into.id, [...(before.get(into.id) ?? []), leg])
    else if (date === pack.meta.endDate) after.push(leg)
  }
  return { before, after }
}

const BAND: Record<Stage['color'], string> = {
  blue: 'linear-gradient(180deg,#B9D4FF,#8FB8FF)',
  snow: 'linear-gradient(180deg,#FFFFFF,#E4F1FD)',
  teal: 'linear-gradient(180deg,#8FE7F2,#5FD3E4)',
  mint: 'linear-gradient(180deg,#9DF0CB,#5FDCA8)',
  yellow: 'linear-gradient(180deg,#FFE58A,#FFC93C)',
  red: 'linear-gradient(180deg,#FF9A9A,#FF6B6B)',
}

function StageLabel({ stage, i, status, pre, today, time }: { stage: Stage; i: number; status: string; pre: boolean; today: string; time: string }) {
  // A stage this child can't see yet (domain/trip/visible): its teaser, length, countdown and clue.
  // On its reveal day the countdown ticks down in hours and minutes.
  if (stage.spot === MYSTERY_SPOT && stage.surprise) {
    const days = daysBetween(stage.startDate, stage.endDate) + 1
    const left = opensIn(stage.surprise.until, { date: today, time })
    return (
      <span>
        <b>
          {i + 1} · 🎁 {stage.name}
        </b>
        <small>{left.minutes === null ? t.trip.mystery(days, left.days) : t.trip.mysteryToday(days, left.minutes)}</small>
        {stage.surprise.clue ? <small>{t.trip.clue(stage.surprise.clue)}</small> : null}
      </span>
    )
  }
  return (
    <>
      <span>
        <b>{t.trip.stage(i + 1, stage.name)}</b>
        <When parts={t.trip.stageWhen(stage.startDate, stage.endDate, daysBetween(stage.startDate, stage.endDate) + 1)} />
        <KidHookLine spot={stage.spot} />
      </span>
      {status === 'now' ? <span className="tag">{t.trip.now}</span> : null}
      {status === 'next' && !pre ? <span className="tag blue">{t.trip.next}</span> : null}
      {i === 0 && pre ? <span className="tag blue">{t.trip.next}</span> : null}
      {status === 'done' ? <span className="tag mint">{t.trip.done}</span> : null}
    </>
  )
}

/** The Today · All days choice, kept while the app is open (tabs remount screens). */
let lastView: 'today' | 'all' | null = null

const TAG: Record<Exclude<PlanTag, null>, { cls: string; text: string }> = {
  booked: { cls: 'tag mint', text: t.trip.booked },
  want: { cls: 'tag yellow', text: t.trip.want },
  surprise: { cls: 'tag purple', text: t.trip.surprise },
}
const DOT: Record<string, string> = { booked: 'var(--red)', want: 'var(--yellow)', surprise: 'var(--purple, #9B6BFF)', leg: 'var(--blue)', none: 'var(--teal, #43C6DB)' }

/** One day (mockup 02): the stage and day, a strip of days, then the plans in time order. */
function DayView({ pack, now, person }: { pack: Pack; now: TripNow; person: Person }) {
  const { meta, itinerary } = pack
  const days = dateRange(meta.startDate, meta.endDate)
  const inTrip = now.clock.today >= meta.startDate && now.clock.today <= meta.endDate
  const [date, setDate] = useState(inTrip ? now.clock.today : meta.startDate)
  const events = useEvents()
  const mine = useMemo(() => myPlaces(events, person.id), [events, person.id])
  const wanted = useMemo(() => new Set(mine.wanted.keys()), [mine])
  const [busy, setBusy] = useState<string | null>(null)
  // Players check in at today's plans that name a place (F5, WI-37).
  // A child who isn't coming (B9) follows the trip read-only: no check-ins, no plans of their own.
  const going = onTrip(meta, person)
  const canCheckIn = going && playsOf(person) && date === now.clock.today
  // A child's own plans (B3), as far as their controls allow; the server checks the same.
  const childEdit = useChildSettings(person.kind === 'child' ? person : null)?.canEdit ?? 'no'
  const canEdit = going ? childEdit : 'no'
  // A surprise that opened today bursts out of its 🎁 the first time it's seen (A3).
  const bursts = useSurpriseBursts(pack, person)
  const editable = (rowId: string) => {
    const item = rowId.startsWith('item:') ? itinerary.dayItems.find((i) => `item:${i.id}` === rowId) : undefined
    if (!item || canEdit === 'no' || item.surprise) return false
    if (item.locked ?? item.booked) return false
    return canEdit === 'unlocked' || item.by === person.id
  }

  const onCheckIn = async (rowId: string, placeId: string) => {
    const place = pack.places.find((p) => p.id === placeId)
    if (!place || busy) return
    setBusy(rowId)
    const res = await checkIn({ place, itemId: rowId.replace(/^item:/, ''), personId: person.id, rules: rewardRules(events), day: playDay(now.nowMs), alreadyFound: mine.found.has(placeId) })
    setBusy(null)
    const toast = useToast.getState().show
    if ('error' in res) toast(res.error === 'denied' ? t.trip.checkinDenied : t.trip.checkinNoFix, 3500)
    else if (res.near) {
      if (res.earned) playsChime()
      toast(t.trip.checkedIn(place.name, res.earned), 3500)
    } else toast(t.trip.notNear(res.metres), 3500)
  }
  const rows = useMemo(() => dayPlan(pack, date, { date: now.clock.today, time: now.clock.time, nowMs: now.nowMs }, wanted), [pack, date, now, wanted])
  const strip = useRef<HTMLDivElement>(null)
  const si = itinerary.stages.findIndex((s) => s.startDate <= date && date <= s.endDate)
  const stage = itinerary.stages[si]
  const dayNo = daysBetween(meta.startDate, date) + 1

  // Keep the chosen day in view in the strip.
  useLayoutEffect(() => {
    strip.current?.querySelector<HTMLElement>('[aria-pressed="true"]')?.scrollIntoView({ inline: 'center', block: 'nearest' })
  }, [date])

  return (
    <>
      <section className="panel summary">
        <div className="summary-head">
          <b>{stage ? t.trip.stage(si + 1, stage.name) : meta.title}</b>
          <span className="mono">{t.trip.dayOf(dayNo, days.length)}</span>
        </div>
        <div className="bar">
          <i style={{ width: `${(dayNo / days.length) * 100}%` }} />
        </div>
      </section>
      <div className="day-strip" ref={strip} role="group" aria-label={t.trip.days}>
        {days.map((d) => (
          <button key={d} className="day-chip press" aria-pressed={d === date} onClick={() => setDate(d)}>
            <small>{fmt.weekdayCaps(d)}</small>
            <b>{Number(d.slice(8))}</b>
          </button>
        ))}
      </div>
      {rows.length ? (
        <ol className="plan-list" aria-label={fmt.day(date)}>
          {rows.map((r) => {
            const dot = r.tag === 'want' || r.tag === 'surprise' ? r.tag : r.kind === 'leg' ? 'leg' : r.tag === 'booked' ? 'booked' : 'none'
            return (
              <li key={r.id} className={`plan-row${r.tag === 'want' ? ' want' : ''}${r.tag === 'surprise' ? ' surprise' : ''}${bursts.has(r.id.replace(/^item:/, '')) ? ' burst-open' : ''}`}>
                <span className="plan-dot" style={{ background: DOT[dot] }} aria-hidden>
                  {r.legKind ? <LegIcon kind={r.legKind} /> : null}
                </span>
                <div
                  className={`panel plan-card${editable(r.id) || r.placeId ? ' press editable' : ''}`}
                  {...(editable(r.id)
                    ? { role: 'button', tabIndex: 0, 'aria-label': `${t.myPlan.edit}: ${r.title}`, onClick: (e: React.MouseEvent) => !(e.target as Element).closest('button') && openOverlay(`my-plan:${date}:${r.id.slice(5)}`) }
                    : r.placeId
                      ? { role: 'button', tabIndex: 0, 'aria-label': r.title, onClick: (e: React.MouseEvent) => !(e.target as Element).closest('button') && openOverlay(`place:${r.placeId}`) }
                      : {})}
                >
                  <div className="plan-top">
                    <span className={`plan-time${r.next ? ' next' : ''}`}>
                      {r.time ? (r.endTime && r.kind === 'leg' ? `${r.time} → ${r.endTime}` : r.time) : t.trip.anyTime}
                      {r.next ? ` · ${t.trip.nextUp}` : ''}
                    </span>
                    {r.tag ? <span className={TAG[r.tag].cls}>{TAG[r.tag].text}</span> : null}
                  </div>
                  <b>{r.title}</b>
                  {r.sub ? <small>{r.sub}</small> : null}
                  {canCheckIn && r.placeId ? (
                    mine.found.has(r.placeId) ? (
                      <span className="tag mint checkin-done">{t.trip.found}</span>
                    ) : (
                      <button className="btn w sm press checkin-btn" disabled={!!busy} onClick={() => void onCheckIn(r.id, r.placeId!)}>
                        {busy === r.id ? t.trip.checking : t.trip.imHere}
                      </button>
                    )
                  ) : null}
                </div>
              </li>
            )
          })}
        </ol>
      ) : (
        <div className="panel plan-empty">
          <b>{stage?.spot === MYSTERY_SPOT ? t.trip.emptyMystery : t.trip.empty}</b>
        </div>
      )}
      {canCheckIn && rows.some((r) => r.placeId) ? <p className="caption">{t.trip.checkinPrivacy}</p> : null}
      {canEdit !== 'no' && stage?.spot !== MYSTERY_SPOT ? (
        <button className="btn y press add-plan" onClick={() => openOverlay(`my-plan:${date}`)}>
          {t.myPlan.addButton(fmt.weekday(date))}
        </button>
      ) : null}
    </>
  )
}

export function TripScreen({ pack, now, person }: { pack: Pack; now: TripNow; person: Person }) {
  const { stages } = pack.itinerary
  const [view, setView] = useState<'today' | 'all'>(() => lastView ?? (now.clock.phase === 'during' ? 'today' : 'all'))
  const pick = setView
  useEffect(() => {
    lastView = view
  }, [view])
  const { before, after } = legsAround(pack)
  const status = stageStatuses(stages, now.clock)
  const pre = now.clock.phase === 'pre'
  const total = daysBetween(pack.meta.startDate, pack.meta.endDate) + 1
  const progress = now.clock.phase === 'during' ? now.clock.dayNumber / total : now.clock.phase === 'after' ? 1 : 0
  // WI-34: 3D in every motion tier (Calm and Still get a still frame: tripSet.setTier).
  const stage3d = useStage((s) => s.status === 'live')
  // §8.3: whichever journey act plays (on its own, a tap, or a chip) is announced.
  const tripAct = useStage((s) => s.tripAct)
  const main = useRef<HTMLElement>(null)
  const route = useRoute()
  const focus = route.overlay?.startsWith('stage:') ? route.overlay.slice(6) : null

  // The 3D chain: one anchor per island (page px), the transport into it, and the scroll.
  const statusKey = status.join(',')
  useLayoutEffect(() => {
    if (!stage3d) return
    const el = main.current
    if (!el) return
    const { before: into } = legsAround(pack)
    const list = pack.itinerary.stages
    const measure = () => {
      const anchors: TripAnchor[] = [...el.querySelectorAll<HTMLElement>('.isle')].map((a, i) => {
        const r = a.getBoundingClientRect()
        const s = list[i]!
        const ride = into.get(s.id)?.find((l) => l.kind === 'train' || l.kind === 'bus')
        const st = pre && i === 0 ? 'next' : (statusKey.split(',')[i] as TripAnchor['status'])
        // WI-41: every leg into the island rides its own vehicle on the link.
        const legsIn = i ? (into.get(s.id) ?? []).map((l) => ({ kind: l.kind, to: shortStop(l.to.name), departsAt: l.departsAt, arrivesAt: l.arrivesAt })) : []
        // A kit route's suggested piece for this hop (Tokyo → Fuji: the viaduct); the router uses it when it fits.
        const hint = i ? pieceHint(list[i - 1]!.spot, s.spot) : undefined
        return { stageId: s.id, spot: s.spot, status: st, x: r.left + r.width * (i % 2 ? 0.68 : 0.32), y: r.top + el.scrollTop + r.height * 0.52, linkIn: i ? (ride?.kind ?? 'train') : null, legsIn, ...(hint ? { pieceHint: hint } : {}) }
      })
      const arrival = into.get(list[0]!.id)?.some((l) => l.kind === 'flight') ?? false
      useStage.getState().set({ trip: { ...useStage.getState().trip, anchors, scroll: el.scrollTop, arrival } })
    }
    // WI-40 #5: a journey chip that would sit under the dock (or the bottom safe area) hides.
    const dockClear = () => {
      const dock = document.querySelector('.dock')?.getBoundingClientRect().top ?? innerHeight
      for (const c of el.querySelectorAll<HTMLElement>('.journey-chip')) {
        const r = c.getBoundingClientRect()
        c.classList.toggle('under-dock', r.bottom > dock - 6)
      }
    }
    measure()
    dockClear()
    const onScroll = () => {
      useStage.getState().set({ trip: { ...useStage.getState().trip, scroll: el.scrollTop } })
      dockClear()
    }
    el.addEventListener('scroll', onScroll, { passive: true })
    const ro = new ResizeObserver(() => {
      measure()
      dockClear()
    })
    ro.observe(el)
    return () => {
      el.removeEventListener('scroll', onScroll)
      ro.disconnect()
    }
  }, [stage3d, pack, statusKey, pre, view])
  useEffect(() => {
    useStage.getState().set({ trip: { ...useStage.getState().trip, focus } })
  }, [focus])
  // WI-41 stretch: on a travel day the vehicles sit where the legs' times put the family (to the minute).
  const minute = Math.floor(now.nowMs / 60_000)
  useEffect(() => {
    useStage.getState().set({ trip: { ...useStage.getState().trip, nowMs: minute * 60_000 } })
  }, [minute])
  // The family rides the train on the 3D chain (Phase F): everyone in the family, in their figures.
  const people = useSession((s) => s.people)
  useEffect(() => {
    const family = (people ?? []).map((p) => figureFor(p, people ?? []))
    useStage.getState().set({ trip: { ...useStage.getState().trip, family } })
  }, [people])
  useEffect(() => {
    useStage.getState().set({ tripEvents: { onPick: (id) => openStop(pack, id) } })
  }, [pack])

  return (
    <main ref={main} className={`screen trip${stage3d ? ' on-stage' : ''}`}>
      <div className="screen-head">
        <h1 className="h1">{t.trip.title}</h1>
        <div className="seg trip-toggle" role="group" aria-label={t.trip.toggle}>
          {(['today', 'all'] as const).map((v) => (
            <button key={v} aria-pressed={view === v} onClick={() => pick(v)}>
              {/* Before the trip there's no "today" on it (WI-30): the first day, and the days. */}
              {v === 'today' ? (pre ? t.trip.firstDay : t.trip.today) : pre ? t.trip.daysPre : t.trip.allDays}
            </button>
          ))}
        </div>
      </div>
      {onTrip(pack.meta, person) ? null : (
        <div className="panel family-trip" role="note">
          <span className="tag blue">{t.trip.familyTripTag}</span>
          <small>{t.trip.familyTrip}</small>
        </div>
      )}
      {view === 'today' ? (
        <DayView pack={pack} now={now} person={person} />
      ) : (
      <>
      <section className="panel summary">
        <div className="summary-head">
          <b>{t.trip.summary(pack.meta.subtitle ?? pack.meta.title, total)}</b>
          {now.clock.phase === 'pre' ? <span className="mono">{t.trip.startsIn(now.clock.daysToGo)}</span> : null}
        </div>
        {stage3d ? (
          <div className="rail" role="progressbar" aria-valuenow={Math.round(progress * 100)} aria-valuemax={100}>
            <i className="rail-train" style={{ left: `${Math.max(4, progress * 100)}%` }}>
              <LegIcon kind="train" />
            </i>
          </div>
        ) : (
          <div className="bar">
            <i style={{ width: `${progress * 100}%` }} />
          </div>
        )}
      </section>
      {stage3d ? (
        <div className="isles">
          {/* §8.3: a journey act (played on its own, by a tap or a chip) is announced, as the Map's are. */}
          <div className="act-ticker" role="status" aria-live="polite">
            {tripAct ? t.trip.journeyAct(tripAct) : null}
          </div>
          {stages.map((s, i) => (
            <div key={s.id}>
              {/* One chip per link, beside the track on the arriving island's side (WI-40 #5). */}
              <div className={`journey-row ${i === 0 ? 'top' : i % 2 ? 'right' : 'left'}`}>
                {before.get(s.id)?.length ? <JourneyChip legs={before.get(s.id)!} link={i ? `${stages[i - 1]!.id}>${s.id}` : undefined} /> : null}
              </div>
              <div className={`isle ${i % 2 ? 'right' : 'left'}`}>
                <button className="isle-label press" onClick={() => openStop(pack, s.id)}>
                  <StageLabel stage={s} i={i} status={status[i]!} pre={pre} today={now.clock.today} time={now.clock.time} />
                </button>
              </div>
            </div>
          ))}
          {after.length ? (
            <div className="journey-row top">
              <JourneyChip legs={after} />
            </div>
          ) : null}
        </div>
      ) : (
        <div className="bands">
          {stages.map((s, i) => (
            <div key={s.id}>
              {before.get(s.id)?.length ? (
                <div className="legs">{before.get(s.id)!.map((l) => <LegChip key={l.id} leg={l} />)}</div>
              ) : null}
              <button
                className={`band press${status[i] === 'now' ? ' is-now' : ''}${status[i] === 'next' || (i === 0 && pre) ? ' is-next' : ''}`}
                style={{ background: BAND[s.color] }}
                onClick={() => openStop(pack, s.id)}
              >
                <StageLabel stage={s} i={i} status={status[i]!} pre={pre} today={now.clock.today} time={now.clock.time} />
              </button>
            </div>
          ))}
          {after.length ? <div className="legs">{after.map((l) => <LegChip key={l.id} leg={l} />)}</div> : null}
        </div>
      )}
      </>
      )}
    </main>
  )
}
